/**
 * Presentation helpers shared by the HUD: in-game time, unit designations, place names and the
 * translation of engine events into terse staff dispatches ("0600 — KESSEL — 3 divisions
 * encircled near Varn") and telegram cards for the big moments.
 *
 * Everything here is pure: it only reads the PlayerView / map it is given, and words it in the
 * language passed in (default: the current one — docs/Localization.md).
 */
import {
  COURIER_HOURS_PER_HEX,
  COURIER_ROAD_FACTOR,
  type Courier,
  dayOf,
  type FactionId,
  type FactionPublic,
  type GameEvent,
  type GameMap,
  type HexGrid,
  type HexId,
  hourOfDay,
  type Order,
  type OrderKind,
  type PlayerView,
  type Posture,
  START_HOUR,
  type TechId,
  type Tick,
  type UnitStatus,
  type UnitType,
} from '@friction/engine';
import { getLocale, inEveryLocale, type Locale, type Localized, tr } from '../i18n';
import {
  cityName,
  type NationRef,
  type Place,
  placeLabel,
  placePhraseOf,
  shortDesignationEn,
  theatreTitle,
  unitName,
  unitShort,
} from '../i18n/names';

// ---- time ---------------------------------------------------------------------------------------

export const pad2 = (n: number): string => String(n).padStart(2, '0');

/** Military clock without separator: "0600". */
export const formatClock = (tick: Tick): string => `${pad2(hourOfDay(tick))}00`;

/** "14:00" */
export const formatHour = (tick: Tick): string => `${pad2(hourOfDay(tick))}:00`;

/** "Day 3 · 14:00" / "Tag 3 · 14:00 Uhr" */
export const formatDate = (tick: Tick, locale: Locale = getLocale()): string =>
  tr(locale)('time.date', { day: dayOf(tick), hour: formatHour(tick) });

/** "D3 1400" / "T3 1400" — compact stamp for lists. */
export const formatStamp = (tick: Tick, locale: Locale = getLocale()): string =>
  tr(locale)('time.stamp', { day: dayOf(tick), clock: formatClock(tick) });

/** Age of a sighting / report: "now", "6h", "2d 4h" / "jetzt", "6 Std.", "2 Tg. 4 Std.". */
export function formatAge(hours: number, locale: Locale = getLocale()): string {
  const t = tr(locale);
  if (hours <= 0) return t('time.now');
  if (hours < 24) return t('time.hours', { h: hours });
  const d = Math.floor(hours / 24);
  const h = hours % 24;
  return h ? t('time.daysHours', { d, h }) : t('time.days', { d });
}

/** Tick at which the given day/hour occurs (inverse of dayOf/hourOfDay). */
export const tickAt = (day: number, hour: number): Tick => (day - 1) * 24 + hour - START_HOUR;

/** First tick strictly after `now` whose hour of day is `hour`. */
export function nextHour(now: Tick, hour: number): Tick {
  let t = now + 1;
  while (hourOfDay(t) !== hour) t++;
  return t;
}

/** An order's execution time for the wording functions, or null when it has none. */
const hhourOf = (tick: Tick): { hour: string; day: number } | null =>
  tick >= 0 ? { hour: formatHour(tick), day: dayOf(tick) } : null;

/** "H 05:00 D4" / "X 05:00 T4" */
export const formatHHour = (tick: Tick, locale: Locale = getLocale()): string =>
  tr(locale)('time.hhour', { hour: formatHour(tick), day: dayOf(tick) });

// ---- colours ------------------------------------------------------------------------------------

export const cssColor = (c: number): string => `#${(c & 0xffffff).toString(16).padStart(6, '0')}`;

/** Linear mix of two 0xRRGGBB colours. */
export function mixColor(a: number, b: number, t: number): number {
  const ch = (s: number) => {
    const x = (a >> s) & 255;
    const y = (b >> s) & 255;
    return Math.round(x + (y - x) * t) << s;
  };
  return ch(16) | ch(8) | ch(0);
}

export const lighten = (c: number, t: number): number => mixColor(c, 0xf3ecdb, t);
export const darken = (c: number, t: number): number => mixColor(c, 0x14110d, t);

// ---- units & orders -----------------------------------------------------------------------------

export const unitTypeLabel = (type: UnitType, locale: Locale = getLocale()): string =>
  tr(locale)(`unit.${type}`);
export const unitTypeBlurb = (type: UnitType, locale: Locale = getLocale()): string =>
  tr(locale)(`unit.blurb.${type}`);
export const statusLabel = (status: UnitStatus, locale: Locale = getLocale()): string =>
  tr(locale)(`status.${status}`);
export const postureLabel = (posture: Posture, locale: Locale = getLocale()): string =>
  tr(locale)(`posture.${posture}`);
export const orderLabel = (kind: OrderKind, locale: Locale = getLocale()): string =>
  tr(locale)(`order.${kind}`);
export const techName = (tech: TechId, locale: Locale = getLocale()): string =>
  tr(locale)(`tech.${tech}.name`);
export const techDescription = (tech: TechId, locale: Locale = getLocale()): string =>
  tr(locale)(`tech.${tech}.desc`);

/** Echelon mark drawn above the counter frame. */
export const echelonOf = (type: UnitType): string => (type === 'hq' ? 'XXXX' : 'XX');

/** "12th Infantry Division" → "12th Inf Div" (English staff abbreviation; see i18n/names.ts). */
export const shortDesignation = shortDesignationEn;

/** Rough courier ETA in hours (mix of road and cross-country legs). */
export function courierEta(c: Courier): number {
  const perHex = COURIER_HOURS_PER_HEX[c.mode] * (0.5 + 0.5 * COURIER_ROAD_FACTOR);
  return Math.max(1, Math.ceil(c.path.length * perHex - c.moveProgress * perHex));
}

// ---- places & factions --------------------------------------------------------------------------

export interface FormatContext {
  view: PlayerView;
  map: GameMap;
  grid: HexGrid;
  /** Recent events, used to recover which sides fought a battle that has just ended. */
  log?: readonly GameEvent[];
}

export const hexLabel = (grid: HexGrid, hex: HexId): string => `${grid.col(hex)}·${grid.row(hex)}`;

/** What a hex is called: the city on it, the nearest city within 3 hexes, else its province. */
export function placeOf(ctx: Pick<FormatContext, 'map' | 'grid'>, hex: HexId): Place {
  const { map, grid } = ctx;
  if (hex < 0 || hex >= grid.count) return { kind: 'front' };
  const here = map.cityAt[hex];
  if (here >= 0) return { kind: 'city', name: map.cities[here].name };
  const near = nearestCity(map, grid, hex, 3);
  if (near) return { kind: 'near', name: near.name };
  const r = map.region[hex];
  return r >= 0 && map.regions[r]
    ? { kind: 'region', name: map.regions[r].name }
    : { kind: 'hex', name: hexLabel(grid, hex) };
}

/** Plain place name: "Dornheim", "Zlatovina Heights" / "Zlatovina-Höhen". */
export const placeName = (
  ctx: Pick<FormatContext, 'map' | 'grid'>,
  hex: HexId,
  locale: Locale = getLocale(),
): string => placeLabel(placeOf(ctx, hex), locale);

/** Prepositional phrase for dispatches: "at Dornheim", "near Dornheim" / "in Dornheim", "bei Dornheim". */
export const placePhrase = (
  ctx: Pick<FormatContext, 'map' | 'grid'>,
  hex: HexId,
  locale: Locale = getLocale(),
): string => placePhraseOf(placeOf(ctx, hex), locale);

function nearestCity(map: GameMap, grid: HexGrid, hex: HexId, maxDist: number) {
  let best: GameMap['cities'][number] | null = null;
  let bestD = maxDist + 1;
  for (const c of map.cities) {
    const d = grid.distance(hex, c.hex);
    // Prefer bigger places on ties: "near Varn" reads better than "near Kleindorf".
    if (d < bestD || (d === bestD && best && c.size > best.size)) {
      best = c;
      bestD = d;
    }
  }
  return best;
}

export function factionOf(view: PlayerView, id: FactionId): FactionPublic | undefined {
  return view.factions.find((f) => f.id === id);
}

/** Name and adjective of a nation, or null when this view does not know it. */
export function nationOf(view: PlayerView, id: FactionId): NationRef | null {
  const f = factionOf(view, id);
  return f ? { name: f.name, adjective: f.adjective } : null;
}

const adjOf = (view: PlayerView, id: FactionId): string | null => factionOf(view, id)?.adjective ?? null;

/**
 * Best-known designation of any unit id (own, allied or enemy contact), as the engine names it —
 * or, for a contact known only by type, "Infantry formation" in the given language.
 */
export function unitLabel(view: PlayerView, id: number, locale: Locale = getLocale()): string | null {
  const own = view.units.find((u) => u.id === id);
  if (own) return own.name;
  const c = view.contacts.find((k) => k.unitId === id);
  if (!c) return null;
  if (c.name) return c.name;
  return tr(locale)(c.type ? `unit.formation.${c.type}` : 'unit.unidentifiedFormation');
}

/** What an order asks for, in the staff's words: "attack Dornheim" / "Angriff auf Dornheim". */
export function orderPhrase(o: Order, place: Place, locale: Locale = getLocale()): string {
  const t = tr(locale);
  return o.kind === 'posture'
    ? t('orderPhrase.posture', { posture: o.posture })
    : t(`orderPhrase.${o.kind}`, { place });
}

// ---- dispatches ---------------------------------------------------------------------------------

export type DispatchCategory = 'front' | 'orders' | 'intel' | 'home';
export type DispatchSeverity = 'routine' | 'notable' | 'urgent';

export interface DispatchLine {
  tick: Tick;
  /** Stencilled keyword printed before the text ("KESSEL", "INTERCEPTED" …). */
  head: string | null;
  text: string;
  category: DispatchCategory;
  severity: DispatchSeverity;
  /** Hex to focus when the line is clicked, or -1. */
  hex: HexId;
}

export interface TelegramSpec {
  title: string;
  body: string;
  stamp: string;
  tone: 'good' | 'bad' | 'neutral';
  hex: HexId;
}

const d = (
  ev: GameEvent,
  category: DispatchCategory,
  severity: DispatchSeverity,
  head: string | null,
  text: string,
  hex: HexId,
): DispatchLine => ({ tick: ev.tick, category, severity, head, text, hex });

/** Translate one engine event into a teletype line from the viewer's point of view. */
export function describeEvent(ev: GameEvent, ctx: FormatContext, locale: Locale = getLocale()): DispatchLine {
  const t = tr(locale);
  const { view } = ctx;
  const me = view.faction;
  const at = (hex: HexId) => placePhrase(ctx, hex, locale);
  const unit = (id: number, fallback = t('unit.aFormation')) =>
    unitShort(unitLabel(view, id, locale) ?? fallback, locale);

  switch (ev.kind) {
    case 'contact': {
      const c = view.contacts.find((k) => k.unitId === ev.enemyUnit);
      const type = c?.type && c.level >= 2 ? c.type : null;
      const sev = c?.type === 'armor' ? 'notable' : 'routine';
      const what = t('tty.what', { type });
      return d(
        ev,
        'front',
        sev,
        t('tty.head.contact'),
        t('tty.contact', { unit: unit(ev.spotter, t('tty.contact.ourPatrol')), what, at: at(ev.hex) }),
        ev.hex,
      );
    }
    case 'battleStarted': {
      if (ev.attackerFaction === me)
        return d(ev, 'front', 'notable', t('tty.head.assault'), t('tty.assault', { at: at(ev.hex) }), ev.hex);
      if (ev.defenderFaction === me)
        return d(
          ev,
          'front',
          'urgent',
          t('tty.head.underAttack'),
          t('tty.underAttack', { adj: adjOf(view, ev.attackerFaction), at: at(ev.hex) }),
          ev.hex,
        );
      return d(ev, 'front', 'routine', null, t('tty.fighting', { at: at(ev.hex) }), ev.hex);
    }
    case 'battleEnded': {
      const start = ctx.log?.find((e) => e.kind === 'battleStarted' && e.battle === ev.battle);
      const sides = start?.kind === 'battleStarted' ? start : null;
      const won = ev.winner === 'attacker';
      const p = { at: at(ev.hex) };
      if (sides?.attackerFaction === me)
        return won
          ? d(ev, 'front', 'notable', t('tty.head.success'), t('tty.success', p), ev.hex)
          : d(ev, 'front', 'notable', t('tty.head.repulsed'), t('tty.repulsed', p), ev.hex);
      if (sides?.defenderFaction === me)
        return won
          ? d(ev, 'front', 'urgent', t('tty.head.breakthrough'), t('tty.breakthrough', p), ev.hex)
          : d(ev, 'front', 'notable', t('tty.head.held'), t('tty.held', p), ev.hex);
      return d(ev, 'front', 'routine', null, t(won ? 'tty.actionCarried' : 'tty.actionHeld', p), ev.hex);
    }
    case 'unitRetreated': {
      if (view.units.some((u) => u.id === ev.unit))
        return d(
          ev,
          'front',
          'notable',
          t('tty.head.withdrawal'),
          t('tty.withdrawal', { unit: unit(ev.unit), from: placeOf(ctx, ev.from) }),
          ev.to,
        );
      return d(ev, 'front', 'routine', t('tty.head.pursuit'), t('tty.pursuit', { at: at(ev.from) }), ev.to);
    }
    case 'unitDestroyed': {
      const name = unitShort(ev.unitName, locale);
      if (ev.faction === me) {
        if (ev.cause === 'surrender')
          return d(
            ev,
            'front',
            'urgent',
            t('tty.head.surrender'),
            t('tty.surrender', { unit: name, at: at(ev.hex) }),
            ev.hex,
          );
        if (ev.cause === 'attrition')
          return d(
            ev,
            'front',
            'urgent',
            t('tty.head.attrition'),
            t('tty.attrition', { unit: name }),
            ev.hex,
          );
        return d(
          ev,
          'front',
          'urgent',
          t('tty.head.loss'),
          t('tty.loss', { unit: name, at: at(ev.hex) }),
          ev.hex,
        );
      }
      return d(
        ev,
        'front',
        'notable',
        t(ev.cause === 'surrender' ? 'tty.head.prisoners' : 'tty.head.destroyed'),
        t('tty.enemyLoss', { unit: name, adj: adjOf(view, ev.faction), cause: ev.cause, at: at(ev.hex) }),
        ev.hex,
      );
    }
    case 'cityCaptured': {
      const city = ctx.map.cities[ev.city];
      const hex = city?.hex ?? -1;
      const name = city ? cityName(city.name, t.locale) : t('place.city');
      if (ev.to === me)
        return d(
          ev,
          'front',
          'notable',
          t('tty.head.captured'),
          t('tty.captured', { city: name, adj: adjOf(view, ev.from) }),
          hex,
        );
      if (ev.from === me)
        return d(
          ev,
          'front',
          'urgent',
          t('tty.head.lost'),
          t('tty.lost', { city: name, adj: adjOf(view, ev.to) }),
          hex,
        );
      return d(
        ev,
        'front',
        'routine',
        null,
        t('tty.fallsTo', { city: name, to: nationOf(view, ev.to) }),
        hex,
      );
    }
    case 'encircled': {
      const n = ev.units.length;
      if (ev.faction === me)
        return d(
          ev,
          'front',
          'urgent',
          t('tty.head.kessel'),
          t('tty.kesselOurs', { n, at: at(ev.hex) }),
          ev.hex,
        );
      return d(
        ev,
        'front',
        'notable',
        t('tty.head.kessel'),
        t('tty.kesselTheirs', { n, adj: adjOf(view, ev.faction), at: at(ev.hex) }),
        ev.hex,
      );
    }
    case 'orderDelivered': {
      const hex = view.units.find((u) => u.id === ev.unit)?.hex ?? -1;
      return d(
        ev,
        'orders',
        'routine',
        t('tty.head.signal'),
        t('tty.acknowledged', { unit: unit(ev.unit), h: ev.delay }),
        hex,
      );
    }
    case 'orderExecuted': {
      const u = view.units.find((x) => x.id === ev.unit);
      return d(ev, 'orders', 'routine', null, t('tty.executing', { unit: unit(ev.unit) }), u?.hex ?? -1);
    }
    case 'courierLost':
      return d(
        ev,
        'orders',
        'urgent',
        t('tty.head.courierMissing'),
        t('tty.courierLost', { unit: unit(ev.unit), at: at(ev.hex) }),
        ev.hex,
      );
    case 'intercept': {
      const ic = ev.intercept;
      const text = t('tty.intercept', {
        method: ic.method,
        unit: unitShort(ic.unitName, locale),
        order: orderPhrase(ic.order, placeOf(ctx, ic.order.target), locale),
        hhour: hhourOf(ic.order.executeAt),
      });
      return d(
        ev,
        'intel',
        'notable',
        t(ic.method === 'captured-courier' ? 'tty.head.intercepted' : 'tty.head.decrypted'),
        text,
        ic.order.target,
      );
    }
    case 'unitProduced': {
      const city = ctx.map.cities[ev.city];
      return d(
        ev,
        'home',
        'routine',
        t('tty.head.mobilised'),
        t('tty.raised', { type: ev.unitType, city: city ? cityName(city.name, t.locale) : t('place.depot') }),
        city?.hex ?? -1,
      );
    }
    case 'techResearched':
      return d(
        ev,
        'home',
        'notable',
        t('tty.head.research'),
        t('tty.researched', { tech: techName(ev.tech, locale) }),
        -1,
      );
    case 'decoyExposed':
      if (view.units.some((u) => u.id === ev.unit))
        return d(
          ev,
          'intel',
          'notable',
          t('tty.head.deception'),
          t('tty.decoyOurs', { at: at(ev.hex) }),
          ev.hex,
        );
      return d(ev, 'intel', 'notable', t('tty.head.decoy'), t('tty.decoyTheirs', { at: at(ev.hex) }), ev.hex);
    case 'hqLost':
      if (ev.faction === me)
        return d(
          ev,
          'front',
          'urgent',
          t('tty.head.hqLost'),
          t('tty.hqLost', { unit: unit(ev.unit, t('unit.anHq')) }),
          -1,
        );
      return d(
        ev,
        'front',
        'notable',
        t('tty.head.hqDestroyed'),
        t('tty.hqDestroyed', { adj: adjOf(view, ev.faction) }),
        -1,
      );
    case 'capitulated':
      if (ev.faction === me)
        return d(ev, 'home', 'urgent', t('tty.head.capitulation'), t('tty.capitulatedUs'), -1);
      return d(
        ev,
        'home',
        'notable',
        t('tty.head.capitulation'),
        t('tty.capitulated', { nation: nationOf(view, ev.faction), by: nationOf(view, ev.by) }),
        -1,
      );
    case 'gameOver':
      return d(ev, 'home', 'urgent', t('tty.head.armistice'), t('tty.armistice'), -1);
    case 'warDeclared':
      return d(ev, 'home', 'urgent', t('tty.head.generalStaff'), t('tty.war', { theatre: ev.theatre }), -1);
    case 'weatherForecast': {
      const region = ctx.map.regions[ev.region];
      const place: Place = region ? { kind: 'region', name: region.name } : { kind: 'front' };
      const when =
        ev.condition === 'fog' ? t('tty.forecast.dawn') : t('tty.forecast.by', { clock: formatClock(ev.at) });
      return d(
        ev,
        'front',
        ev.condition === 'storm' || ev.condition === 'blizzard' ? 'notable' : 'routine',
        t('tty.head.met'),
        t('tty.forecast', { what: t(`wx.cond.${ev.condition}`), region: place, when, chance: ev.chance }),
        ev.hex,
      );
    }
    case 'weatherChange':
      return d(ev, 'front', 'notable', t(`wx.change.${ev.change}.head`), t(`wx.change.${ev.change}`), ev.hex);
    default: {
      // Event kinds added to the engine later still print, just without bespoke wording.
      const e = ev as GameEvent;
      return d(e, 'home', 'routine', null, e.kind.replace(/([A-Z])/g, ' $1').toLowerCase(), -1);
    }
  }
}

/** Big moments get a telegram card; everything else stays on the teletype. */
export function telegramFor(
  ev: GameEvent,
  ctx: FormatContext,
  locale: Locale = getLocale(),
): TelegramSpec | null {
  const t = tr(locale);
  const { view } = ctx;
  const me = view.faction;
  switch (ev.kind) {
    case 'cityCaptured': {
      if (ev.to !== me && ev.from !== me) return null;
      const city = ctx.map.cities[ev.city];
      const name = city ? cityName(city.name, t.locale) : t('place.city');
      const hex = city?.hex ?? -1;
      return ev.to === me
        ? {
            title: t('tg.cityCaptured.title'),
            body: t('tg.cityCaptured', {
              city: name,
              adj: adjOf(view, ev.from),
              size: (city?.size ?? 2) as 1 | 2 | 3,
            }),
            stamp: t('tg.cityCaptured.stamp'),
            tone: 'good',
            hex,
          }
        : {
            title: t('tg.cityLost.title'),
            body: t('tg.cityLost', { city: name, adj: adjOf(view, ev.to) }),
            stamp: t('tg.cityLost.stamp'),
            tone: 'bad',
            hex,
          };
    }
    case 'encircled': {
      const n = ev.units.length;
      const place = placePhrase(ctx, ev.hex, locale);
      return ev.faction === me
        ? {
            title: t('tg.kesselOurs.title'),
            body: t('tg.kesselOurs', { n, at: place }),
            stamp: t('tg.kessel.stamp'),
            tone: 'bad',
            hex: ev.hex,
          }
        : {
            title: t('tg.kesselTheirs.title'),
            body: t('tg.kesselTheirs', { n, adj: adjOf(view, ev.faction), at: place }),
            stamp: t('tg.kessel.stamp'),
            tone: 'good',
            hex: ev.hex,
          };
    }
    case 'intercept': {
      const ic = ev.intercept;
      const courier = ic.method === 'captured-courier';
      return {
        title: t(courier ? 'tg.courierCaptured.title' : 'tg.radioDecrypt.title'),
        body: t('tg.intercept', {
          unit: unitName(ic.unitName, locale),
          order: orderPhrase(ic.order, placeOf(ctx, ic.order.target), locale),
          hhour: hhourOf(ic.order.executeAt),
        }),
        stamp: t(courier ? 'tg.intercepted.stamp' : 'tg.decrypted.stamp'),
        tone: 'good',
        hex: ic.order.target,
      };
    }
    case 'courierLost':
      return {
        title: t('tg.courierLost.title'),
        body: t('tg.courierLost', {
          unit: unitName(unitLabel(view, ev.unit, locale) ?? t('unit.aFormation'), locale),
        }),
        stamp: t('tg.courierLost.stamp'),
        tone: 'bad',
        hex: ev.hex,
      };
    case 'techResearched':
      return {
        title: t('tg.research.title'),
        body: t('tg.research', { name: techName(ev.tech, locale), desc: techDescription(ev.tech, locale) }),
        stamp: t('tg.research.stamp'),
        tone: 'neutral',
        hex: -1,
      };
    case 'hqLost': {
      if (ev.faction !== me) return null;
      const label = unitLabel(view, ev.unit, locale);
      return {
        title: t('tg.hqLost.title'),
        body: t('tg.hqLost', { unit: label ? unitName(label, locale) : null }),
        stamp: t('tg.hqLost.stamp'),
        tone: 'bad',
        hex: -1,
      };
    }
    case 'warDeclared':
      return {
        title: t('tg.war.title'),
        body: t('tg.war', { theatre: ev.theatre }),
        stamp: t('tg.war.stamp'),
        tone: 'neutral',
        hex: -1,
      };
    case 'weatherChange':
      if (ev.change !== 'mud' && ev.change !== 'frost' && ev.change !== 'thaw') return null;
      return {
        title: t(`tg.${ev.change}.title`),
        body: t(`tg.${ev.change}`),
        stamp: t('tg.met.stamp'),
        tone: 'neutral',
        hex: -1,
      };
    case 'capitulated':
      return ev.faction === me
        ? {
            title: t('tg.capitulation.title'),
            body: t('tg.capitulationUs'),
            stamp: t('tg.capitulation.stamp'),
            tone: 'bad',
            hex: -1,
          }
        : {
            title: t('tg.capitulation.title'),
            body: t('tg.capitulation', { nation: nationOf(view, ev.faction), by: nationOf(view, ev.by) }),
            stamp: t('tg.capitulation.stamp'),
            tone: ev.by === me ? 'good' : 'neutral',
            hex: -1,
          };
    default:
      return null;
  }
}

/** A telegram worded in every language, or null when the event does not make one. */
export function telegramInEveryLocale(ev: GameEvent, ctx: FormatContext): Localized<TelegramSpec> | null {
  if (!telegramFor(ev, ctx, 'en')) return null;
  return inEveryLocale((l) => telegramFor(ev, ctx, l) as TelegramSpec);
}

/** Theatre title in a language ("The Varnian Marches" / "Die Varnischen Marken"). */
export const theatreName = (name: string, locale: Locale = getLocale()): string => theatreTitle(name, locale);
